import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FirmPortalPermission, PermissionsState } from '@anduin/auth';
import { Store } from '@ngxs/store';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { OutboundEmailRow } from '../../models/outbound-emails.model';
import { EMAIL_MISSING_INVOICE_ID, USER_HAS_NOT_PERMISSION_TO_RESEND_EMAIL } from './utils';
import { isEmpty } from '@anduin/shared';

type OnClickFn = (data: OutboundEmailRow) => Promise<unknown> | void;

export interface ResendCellRendererParams extends ICellRendererParams<OutboundEmailRow> {
  onClick?: OnClickFn;
}

@Component({
  selector: 'app-resend-icon-cell-template',
  templateUrl: './resend-icon-cell-template.component.html',
  styleUrl: './resend-icon-cell-template.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResendIconCellTemplateComponent implements ICellRendererAngularComp {
  private readonly store = inject(Store);

  params: ResendCellRendererParams;
  onClickFn: OnClickFn;

  data = signal<OutboundEmailRow>(undefined);
  isLoading = signal<boolean>(false);
  tooltip = computed(() => this._tooltipMessage());

  canResendEmails = this.store.selectSignal(PermissionsState.hasPermission([FirmPortalPermission.EMAIL_MANAGEMENT_RESEND_EMAILS]));

  agInit(params: ResendCellRendererParams): void {
    this.params = params;
    this.data.set(params.data);
    this.onClickFn = params.onClick ?? this.emptyFn;
  }

  async onClick(): Promise<void> {
    if (this.isLoading() || this.tooltip() !== undefined) {
      return;
    }
    const result = this.onClickFn(this.data());
    if (result instanceof Promise) {
      this.isLoading.set(true);
      try {
        await result;
      } finally {
        this.isLoading.set(false);
      }
    }
  }

  private _tooltipMessage(): string {
    const data = this.data();

    if (!data) {
      return undefined;
    }

    if (!this.canResendEmails()) {
      return USER_HAS_NOT_PERMISSION_TO_RESEND_EMAIL;
    }

    if (data.emailType === 'Invoice' && isEmpty(data.invoiceIds)) {
      return EMAIL_MISSING_INVOICE_ID;
    }

    return undefined;
  }

  emptyFn() {
    // I'm doing this only to remove the eslint warning
  }

  refresh(): boolean {
    return false;
  }
}
